<template>
  <main class="bill-page">
    <AppNavBar :title="t('card.billDetail')">
      <template #right>
        <button type="button" class="filter-btn" @click="showSheet = true">
          <app-image src="@/assets/images/cardImage/time_icon.png" alt="" />
        </button>
      </template>
    </AppNavBar>

    <van-pull-refresh v-model="refreshing" @refresh="refreshList">
      <van-list
        :loading="loading"
        :finished="finished"
        :finished-text="list.length ? t('common.noMore') : ''"
        @load="loadList"
      >
        <AppEmptyState
          v-if="!list.length && !loading"
          :description="t('card.noTrade')"
        />
        <button
          v-for="item in list"
          :key="item.id || item.createTime"
          class="bill-card"
          type="button"
          @click="openTrade(item)"
        >
          <div>
            <div data-strong class="data-label">
              {{ item.typeMsg || item.remark || "--" }}
            </div>
            <span>{{ renderTradeTime(item.createTime) }}</span>
          </div>
          <div>
            <div class="bill-card__summary">
              <div
                data-strong
                :class="{ positive: Number(item.amount || 0) > 0 }"
              >
                {{ renderTradeAmount(item) }}
              </div>
              <!-- <span :class="['bill-status', `bill-status--${statusKey(item.status)}`]">{{ statusText(item.status) }}</span> -->
            </div>
            <app-image
              class="bill-card__arrow"
              src="@/assets/images/rightArrow_icon.png"
              alt=""
            />
          </div>
        </button>
      </van-list>
    </van-pull-refresh>

    <van-popup
      v-model:show="showSheet"
      round
      position="bottom"
      class="range-popup"
      @open="syncPendingRange"
    >
      <div class="range-popup__header">
        <div class="range-popup__placeholder" />
        <div class="range-popup__title-wrap">
          <h3>{{ t("card.selectTimeRange") }}</h3>
          <p>{{ t("card.timeRangeTip") }}</p>
        </div>
        <button
          type="button"
          class="range-popup__close"
          @click="showSheet = false"
        >
          <van-icon name="cross" />
        </button>
      </div>

      <div class="range-popup__options">
        <button
          v-for="item in rangeActions"
          :key="item.value"
          type="button"
          class="range-popup__option"
          :class="{
            'range-popup__option--active': pendingRange === item.value,
          }"
          @click="pendingRange = item.value"
        >
          <span>{{ item.name }}</span>
          <app-image
            v-if="pendingRange === item.value"
            class="range-popup__checked-icon"
            :src="checkedIcon"
            alt=""
          />
          <van-icon v-else name="circle" />
        </button>
      </div>

      <div class="range-popup__footer">
        <button
          type="button"
          class="range-popup__action range-popup__action--reset"
          @click="resetRange"
        >
          {{ t("common.reset") }}
        </button>
        <button
          type="button"
          class="range-popup__action range-popup__action--confirm"
          @click="confirmRange"
        >
          {{ t("common.confirm") }}
        </button>
      </div>
    </van-popup>
  </main>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useCardBillStore } from "@/stores/card-bill";
import { useProfileStore } from "@/stores/profile";
import { getRouteState } from "@/utils/route-state";
import { formatCardTimestamp, formatTradeAmount } from "@/view/card/utils";
import checkedIcon from "@/assets/images/checkbox.png";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const profileStore = useProfileStore();
const cardBillStore = useCardBillStore();
const refreshing = ref(false);
const showSheet = ref(false);
const pendingRange = ref(Number(cardBillStore.lastDays) || 30);
const pageState = computed(() => getRouteState(route, "card-bill", {}));
const currentCardId = computed(() => String(pageState.value.cardId || ""));

const list = computed(() => cardBillStore.list);
const loading = computed(() => cardBillStore.loading);
const finished = computed(() => cardBillStore.finished);
const rangeActions = computed(() => [
  { name: t("card.last30Days"), value: 30 },
  { name: t("card.last90Days"), value: 90 },
  { name: t("card.last180Days"), value: 180 },
]);

function renderTradeTime(value) {
  return formatCardTimestamp(value);
}

function renderTradeAmount(item) {
  return formatTradeAmount(item);
}

function statusText(status) {
  if (Number(status) === 1) return t("card.success");
  if (Number(status) === 2) return t("card.failed");
  if (Number(status) === 3) return t("card.processing");
  return "";
}

function statusKey(status) {
  if (Number(status) === 1) return "success";
  if (Number(status) === 2) return "failed";
  if (Number(status) === 3) return "processing";
  return "default";
}

async function refreshList() {
  await loadList(true);
  refreshing.value = false;
}

async function loadList(reset = false) {
  await cardBillStore.fetchNext({
    address: profileStore.platformAddress || null,
    cardId: currentCardId.value,
    reset,
  });
}

function syncPendingRange() {
  pendingRange.value = Number(cardBillStore.lastDays) || 30;
}

function resetRange() {
  pendingRange.value = 30;
}

function confirmRange() {
  cardBillStore.setLastDays(pendingRange.value);
  showSheet.value = false;
  loadList(true);
}

function openTrade(item) {
  cardBillStore.setCurrentTrade(item);
  router.push("/card/bill/detail");
}

watch(
  () => [currentCardId.value, profileStore.platformAddress],
  () => {
    cardBillStore.reset(currentCardId.value);
    loadList(true);
  },
  { immediate: true },
);
</script>

<style scoped lang="scss">
.bill-page {
  min-height: var(--app-viewport-height);
  padding: 0 14px 24px;
  background: #f5f5f5;
}

.filter-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;

  .app-image {
    width: 22px;
    height: 22px;
    object-fit: contain;
  }
}

.bill-card {
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-top: 8px;
  padding: 14px 12px;
  border: 0;
  border-radius: 8px;
  background: #ffffff;
  color: var(--wp-color-text);
  text-align: left;

  [data-strong],
  span {
    display: block;
  }

  .data-label {
    font-weight: 500 !important;
  }

  [data-strong] {
    font-size: 15px;
    font-weight: 700;
  }

  span {
    margin-top: 4px;
    color: var(--wp-color-sub-text);
    font-size: 12px;
  }

  > div:last-child {
    display: flex;
    align-items: center;
    gap: 8px;
    text-align: right;

    .bill-card__summary {
      min-width: 0;

      [data-strong] {
        color: #f53f3f;
        font-weight: 700;
      }

      [data-strong].positive {
        color: #07b077;
      }
    }

    .bill-card__arrow {
      width: 9px;
      height: 14px;
      flex: 0 0 9px;
      object-fit: contain;
    }
  }
}

.bill-status {
  &--success {
    color: #999999;
  }

  &--failed {
    color: #f53f3f;
  }

  &--processing {
    color: #165dff;
  }
}

.range-popup {
  overflow: hidden;
  background: #ffffff;
}

.range-popup__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 16px 10px;
}

.range-popup__placeholder,
.range-popup__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
}

.range-popup__close {
  padding: 0;
  border: 0;
  background: transparent;
  color: #999999;
  font-size: 20px;
}

.range-popup__title-wrap {
  flex: 1;
  text-align: center;

  h3 {
    margin: 0;
    color: #111111;
    font-size: 18px;
    font-weight: 700;
    line-height: 26px;
  }

  p {
    margin: 4px 0 0;
    color: #999999;
    font-size: 13px;
    line-height: 18px;
  }
}

.range-popup__checked-icon {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
}

.range-popup__option--active {
  background: #dfeaff;
  color: #000;
  font-weight: 500;
}

.range-popup__options {
  padding: 0 0 12px;
}

.range-popup__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 54px;
  padding: 0 18px;
  border: 0;
  background: #ffffff;
  color: #666;
  text-align: left;

  span {
    font-size: 16px;
    line-height: 22px;
  }

  .van-icon {
    color: #2667ff;
    font-size: 20px;
  }
}

.range-popup__option--active {
  background: #dfeaff;
  color: #000;
  font-weight: 500;
}

.range-popup__footer {
  display: flex;
  gap: 16px;
  padding: 8px 16px calc(20px + env(safe-area-inset-bottom));
}

.range-popup__action {
  flex: 1;
  height: 44px;
  border: 0;
  border-radius: 22px;
  font-size: 18px;
  font-weight: 500;
}

.range-popup__action--reset {
  background: #dfeaff;
  color: #2667ff;
}

.range-popup__action--confirm {
  background: #2667ff;
  color: #ffffff;
}
</style>
